import { getTranslations } from 'next-intl/server';
import { ReadonlyURLSearchParams } from 'next/navigation';

import { ImageDto, PhotoTag } from '@/lib/image.dto';
import { LocationPricesDto } from '@/lib/locations/dto/location-price.dto';
import { LocationProductsDto } from '@/lib/locations/dto/location-products.dto';
import { Frequency } from '@/models/Frequency.enum';
import { Products } from '@/models/Products.enum';
import { shouldDisplayProduct } from '@/utils/location-products';

import { PremiumAmenity } from './types';

export const PREMIUM_AMENITIES_CONFIG: PremiumAmenity[] = [
  {
    label: 'comfy-lounges',
    tags: [
      [PhotoTag.CommonArea, PhotoTag.HasSoftSeating, PhotoTag.HasPeople],
      [PhotoTag.CommonArea, PhotoTag.HasSoftSeating],
      [PhotoTag.CommonArea],
    ],
    fallbackImage: '/public-s3/images/location-comfy-common-lounge.webp',
  },
  {
    label: 'cafe',
    tags: [[PhotoTag.Cafe, PhotoTag.HasPeople], [PhotoTag.Cafe]],
    fallbackImage: '/public-s3/images/location-cafe.webp',
  },
  {
    label: 'meeting-rooms',
    tags: [[PhotoTag.MeetingRoomLarge], [PhotoTag.MeetingRoom]],
    fallbackImage: '/public-s3/images/location-meeting-room.webp',
  },
  {
    label: 'phone-booths',
    tags: [
      [PhotoTag.HasPhoneBooths, PhotoTag.HasPeople],
      [PhotoTag.HasPhoneBooths],
    ],
    fallbackImage: '/public-s3/images/location-phone-booths.webp',
  },
];

const findImageByTags = (images: ImageDto[], tagCombination: PhotoTag[]) => {
  return images.find((image) =>
    tagCombination.every((tag) => image.tags?.includes(tag))
  );
};

export function getImageForLocationAmenity(
  images: ImageDto[],
  tags: PhotoTag[][]
) {
  const image = tags
    .map((tagCombination) => findImageByTags(images, tagCombination))
    .find(Boolean);

  return image || null;
}

export type OnDemandProduct = {
  title: string;
  prices: { price: number; frequency: Frequency }[];
  tag: string;
  value: Products.Office | Products.MeetingRoom | Products.Coworking;
  canBuyOnline?: boolean;
};

export async function getProductsToDisplay({
  locale,
  locationProducts,
  locationPrices,
}: {
  locale: string;
  locationProducts: LocationProductsDto;
  locationPrices: LocationPricesDto;
}): Promise<OnDemandProduct[]> {
  const result: OnDemandProduct[] = [];
  const t = await getTranslations({
    locale,
    namespace: 'location.on-demand',
  });

  const shouldDisplayOffice =
    locationProducts.dedicatedOffice.availableAtLocation;
  const shouldDisplayMeetingRoom =
    !!locationProducts.meetingRoom.availableAtLocation &&
    !!locationProducts.meetingRoom.currentlySellable;

  const shouldDisplayCoworkingDayPass = shouldDisplayProduct(
    locationProducts.coworkingDayPass
  );
  const shouldDisplayAccess = locationProducts.access.availableAtLocation;

  const shouldDisplayCoworking =
    shouldDisplayCoworkingDayPass || shouldDisplayAccess;

  if (shouldDisplayOffice) {
    const officePrice = locationPrices.lowestPrices.office.perMonth;
    result.push({
      title: t(`${Products.Office}.title`),
      prices: [
        officePrice != null && {
          price: officePrice,
          frequency: Frequency.Monthly,
        },
      ].filter((x) => x !== false),
      tag: t(`${Products.Office}.tag`),
      value: Products.Office,
      canBuyOnline:
        !!locationProducts.dedicatedOffice.currentlySellable &&
        !!locationProducts.dedicatedOffice.canBuyOnline,
    });
  }

  if (shouldDisplayCoworking) {
    const { perDay, perMonth } = locationPrices.lowestPrices.coworking;

    const coworkingPrices = [];
    if (perDay !== undefined && shouldDisplayCoworkingDayPass) {
      coworkingPrices.push({
        price: perDay,
        frequency: Frequency.Daily,
      });
    }
    if (perMonth !== undefined && shouldDisplayAccess) {
      coworkingPrices.push({
        price: perMonth,
        frequency: Frequency.Monthly,
      });
    }

    result.push({
      title: t(`${Products.Coworking}.title`),
      prices: coworkingPrices,
      tag: t(`${Products.Coworking}.tag`),
      value: Products.Coworking,
    });
  }

  if (shouldDisplayMeetingRoom) {
    const { perHour, perDay } = locationPrices.lowestPrices.meetingRoom;
    result.push({
      title: t(`${Products.MeetingRoom}.title`),
      prices: [
        perHour !== undefined && {
          price: perHour,
          frequency: Frequency.Hourly,
        },
        perDay !== undefined && {
          price: perDay,
          frequency: Frequency.Daily,
        },
      ].filter((x) => x !== false),
      tag: t(`${Products.MeetingRoom}.tag`),
      value: Products.MeetingRoom,
    });
  }

  return result;
}

export function getProductFromSearchParams(
  searchparams: ReadonlyURLSearchParams,
  products: OnDemandProduct[]
): Products {
  const product = searchparams.get('product');

  if (product && Object.values(Products).includes(product as Products)) {
    return product as Products;
  }

  return Object.values(products)[0].value;
}
